import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import { getSensitiveTopicLabel } from '../../utils/sensitiveTopicsTranslations';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import {
  Search,
  Upload,
  Download,
  Loader2,
  CheckCircle2,
  XCircle,
  Globe,
  Mail,
  User,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MediaSearchTab = () => {
  const { language } = useLanguage();
  
  // State
  const [textInput, setTextInput] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  const t = language === 'es' ? {
    title: 'Búsqueda Masiva de Medios',
    description: 'Busca dominios en tu catálogo o filtra por email del editor.',
    textTab: 'Pegar URLs',
    fileTab: 'Subir Archivo',
    textPlaceholder: 'Pega las URLs aquí (una por línea):\n\nhttps://ejemplo.com\nwww.otrodominio.es\nmidominio.com',
    emailFilterLabel: 'Filtrar por Email del Editor',
    emailFilterDesc: 'Introduce un email para ver todos los sitios de ese editor/proveedor',
    emailPlaceholder: 'editor@ejemplo.com',
    searchByEmail: 'Buscar por Email',
    search: 'Buscar URLs',
    searching: 'Buscando...',
    selectFile: 'Seleccionar archivo...',
    results: 'Resultados',
    found: 'Encontrados',
    notFound: 'No Encontrados',
    totalSearched: 'Total Buscados',
    sitesOfEditor: 'Sitios del Editor',
    downloadExcel: 'Descargar Excel',
    domain: 'Dominio',
    price: 'Precio Compra',
    pvp: 'PVP',
    owner: 'Email',
    dr: 'DR',
    traffic: 'Traffic',
    category: 'Categoría',
    country: 'País',
    sensitive: 'Temáticas',
    instructions: '¿Cómo funciona?',
    instructionEmail: 'Filtro por email: Introduce el email de un editor para ver todos sus sitios',
    instructionUrls: 'Búsqueda de URLs: Pega dominios o sube archivo para verificar cuáles están en tu catálogo',
    noResults: 'No hay resultados',
    noSitesFound: 'No se encontraron sitios para este email',
  } : {
    title: 'Bulk Media Search',
    description: 'Search domains in your catalog or filter by editor email.',
    textTab: 'Paste URLs',
    fileTab: 'Upload File',
    textPlaceholder: 'Paste URLs here (one per line):\n\nhttps://example.com\nwww.anotherdomain.com\nmydomain.com',
    emailFilterLabel: 'Filter by Editor Email',
    emailFilterDesc: 'Enter an email to see all sites from that editor/provider',
    emailPlaceholder: 'editor@example.com',
    searchByEmail: 'Search by Email',
    search: 'Search URLs',
    searching: 'Searching...',
    selectFile: 'Select file...',
    results: 'Results',
    found: 'Found',
    notFound: 'Not Found',
    totalSearched: 'Total Searched',
    sitesOfEditor: 'Editor Sites',
    downloadExcel: 'Download Excel',
    domain: 'Domain',
    price: 'Purchase Price',
    pvp: 'Sale Price',
    owner: 'Email',
    dr: 'DR',
    traffic: 'Traffic',
    category: 'Category',
    country: 'Country',
    sensitive: 'Topics',
    instructions: 'How does it work?',
    instructionEmail: 'Email filter: Enter an editor email to see all their sites',
    instructionUrls: 'URL search: Paste domains or upload file to check which are in your catalog',
    noResults: 'No results',
    noSitesFound: 'No sites found for this email',
  };

  // Search by email
  const handleEmailSearch = async () => {
    if (!emailFilter.trim() || !emailFilter.includes('@')) {
      toast.error(language === 'es' ? 'Introduce un email válido' : 'Enter a valid email');
      return;
    }
    
    setSearching(true);
    setResults(null);
    try {
      const response = await axios.post(`${API}/media-search/search-by-email`, {
        email: emailFilter.trim()
      });
      setResults(response.data);
      if (response.data.total_found > 0) {
        toast.success(`${response.data.total_found} ${language === 'es' ? 'sitios encontrados' : 'sites found'}`);
      } else {
        toast.info(t.noSitesFound);
      }
    } catch (error) {
      console.error('Email search error:', error);
      toast.error(error.response?.data?.detail || 'Error en la búsqueda');
    } finally {
      setSearching(false);
    }
  };

  // Search from text (URLs)
  const handleTextSearch = async () => {
    if (!textInput.trim()) {
      toast.error(language === 'es' ? 'Introduce al menos una URL' : 'Enter at least one URL');
      return;
    }
    
    setSearching(true);
    setResults(null);
    try {
      const response = await axios.post(`${API}/media-search/search-text`, {
        text: textInput
      });
      setResults(response.data);
      toast.success(`${response.data.total_found} ${t.found}, ${response.data.total_not_found} ${t.notFound}`);
    } catch (error) {
      console.error('Search error:', error);
      toast.error(error.response?.data?.detail || 'Error en la búsqueda');
    } finally {
      setSearching(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setSearching(true);
    setResults(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/media-search/search-file`, formData);
      setResults(response.data);
      toast.success(`${response.data.total_found} ${t.found}, ${response.data.total_not_found} ${t.notFound}`);
    } catch (error) {
      console.error('File search error:', error);
      toast.error(error.response?.data?.detail || 'Error en la búsqueda');
    } finally {
      setSearching(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Download results as Excel
  const handleDownloadExcel = async () => {
    if (!results?.search_id) return;
    
    try {
      const response = await axios.get(`${API}/media-search/export/${results.search_id}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = results.search_type === 'email' 
        ? `sitios_editor_${results.search_email?.split('@')[0] || 'export'}_${Date.now()}.xlsx`
        : `busqueda_medios_${Date.now()}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(language === 'es' ? '¡Excel descargado!' : 'Excel downloaded!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(language === 'es' ? 'Error al descargar' : 'Download error');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Search className="h-6 w-6" />
          {t.title}
        </h2>
        <p className="text-muted-foreground">{t.description}</p>
      </div>

      {/* Instructions */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Globe className="h-5 w-5 text-blue-600" />
            </div>
            <div className="space-y-1">
              <h4 className="font-medium text-blue-900">{t.instructions}</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li className="flex items-center gap-2">
                  <Mail className="h-3 w-3" />
                  {t.instructionEmail}
                </li>
                <li className="flex items-center gap-2">
                  <Globe className="h-3 w-3" />
                  {t.instructionUrls}
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Filter - Always visible */}
      <Card className="border-purple-200">
        <CardContent className="p-4">
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <Mail className="h-4 w-4 text-purple-600" />
              {t.emailFilterLabel}
            </label>
            <p className="text-xs text-muted-foreground">{t.emailFilterDesc}</p>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="email"
                  placeholder={t.emailPlaceholder}
                  value={emailFilter}
                  onChange={(e) => setEmailFilter(e.target.value)}
                  className="pl-10"
                  onKeyDown={(e) => e.key === 'Enter' && handleEmailSearch()}
                  disabled={searching}
                />
              </div>
              <Button 
                onClick={handleEmailSearch} 
                disabled={searching || !emailFilter.trim()}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {searching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    {t.searchByEmail}
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">
            {language === 'es' ? 'O buscar por URLs' : 'Or search by URLs'}
          </span>
        </div>
      </div>

      {/* URL Search Options */}
      <Tabs defaultValue="text" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="text" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            {t.textTab}
          </TabsTrigger>
          <TabsTrigger value="file" className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            {t.fileTab}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="text" className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <Textarea
                  placeholder={t.textPlaceholder}
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  rows={6}
                  className="font-mono text-sm"
                  disabled={searching}
                />
                <Button 
                  onClick={handleTextSearch} 
                  disabled={searching || !textInput.trim()}
                  className="w-full"
                >
                  {searching ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4 mr-2" />
                  )}
                  {searching ? t.searching : t.search}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="file" className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={handleFileUpload}
                  disabled={searching}
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={searching}
                  className="w-full h-24 border-dashed border-2"
                >
                  {searching ? (
                    <div className="flex flex-col items-center">
                      <Loader2 className="h-8 w-8 mb-2 animate-spin" />
                      <span>{t.searching}</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Upload className="h-8 w-8 mb-2 text-muted-foreground" />
                      <span>{t.selectFile}</span>
                      <span className="text-xs text-muted-foreground">Excel (.xlsx) o CSV</span>
                    </div>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Results */}
      {results && (
        <div className="space-y-4">
          {/* Summary Cards - Different layout for email search */}
          {results.search_type === 'email' ? (
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <User className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-purple-900">{t.sitesOfEditor}</p>
                      <p className="text-sm text-purple-700">{results.search_email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-purple-600">{results.total_found}</p>
                    <p className="text-sm text-purple-600">{language === 'es' ? 'sitios' : 'sites'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-3 gap-4">
              <Card className="bg-slate-50">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold">{results.total_searched}</p>
                  <p className="text-sm text-muted-foreground">{t.totalSearched}</p>
                </CardContent>
              </Card>
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{results.total_found}</p>
                  <p className="text-sm text-green-600">{t.found}</p>
                </CardContent>
              </Card>
              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-red-600">{results.total_not_found}</p>
                  <p className="text-sm text-red-600">{t.notFound}</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Download Button */}
          <div className="flex justify-end">
            <Button onClick={handleDownloadExcel} variant="outline">
              <Download className="h-4 w-4 mr-2" />
              {t.downloadExcel}
            </Button>
          </div>

          {/* Found Items Table */}
          {results.found?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className={`flex items-center gap-2 ${results.search_type === 'email' ? 'text-purple-600' : 'text-green-600'}`}>
                  {results.search_type === 'email' ? <User className="h-5 w-5" /> : <CheckCircle2 className="h-5 w-5" />}
                  {results.search_type === 'email' ? t.sitesOfEditor : t.found} ({results.found.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-auto" style={{ maxHeight: '500px' }}>
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-slate-100 z-10">
                      <tr className="border-b">
                        <th className="h-10 px-4 text-left font-semibold">#</th>
                        <th className="h-10 px-4 text-left font-semibold">{t.domain}</th>
                        <th className="h-10 px-4 text-right font-semibold">{t.price}</th>
                        <th className="h-10 px-4 text-right font-semibold">{t.pvp}</th>
                        <th className="h-10 px-4 text-right font-semibold">{t.dr}</th>
                        <th className="h-10 px-4 text-right font-semibold">{t.traffic}</th>
                        {results.search_type === 'email' && (
                          <>
                            <th className="h-10 px-4 text-left font-semibold">{t.category}</th>
                            <th className="h-10 px-4 text-left font-semibold">{t.country}</th>
                          </>
                        )}
                        <th className="h-10 px-4 text-left font-semibold">{t.sensitive}</th>
                        {results.search_type !== 'email' && (
                          <th className="h-10 px-4 text-left font-semibold">{t.owner}</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {results.found.map((item, idx) => (
                        <tr key={idx} className="border-b hover:bg-slate-50">
                          <td className="p-4 text-muted-foreground font-mono text-xs">{idx + 1}</td>
                          <td className="p-4 font-medium">{item.domain}</td>
                          <td className="p-4 text-right">{item.price || 0}</td>
                          <td className="p-4 text-right">{item.pvp || 0}</td>
                          <td className="p-4 text-right">{item.dr || '-'}</td>
                          <td className="p-4 text-right">{item.traffic?.toLocaleString() || '-'}</td>
                          {results.search_type === 'email' && (
                            <>
                              <td className="p-4 text-sm">{item.category || '-'}</td>
                              <td className="p-4 text-sm">{item.country || '-'}</td>
                            </>
                          )}
                          <td className="p-4">
                            {item.sensitive_topics?.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {item.sensitive_topics.slice(0, 3).map((topic, i) => (
                                  <Badge key={i} variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 text-xs">
                                    {getSensitiveTopicLabel(topic, language)}
                                  </Badge>
                                ))}
                                {item.sensitive_topics.length > 3 && (
                                  <Badge variant="outline" className="text-xs">+{item.sensitive_topics.length - 3}</Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          {results.search_type !== 'email' && (
                            <td className="p-4 text-muted-foreground select-all cursor-text">{item.owner || '-'}</td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Not Found List - Only for domain search */}
          {results.search_type !== 'email' && results.not_found?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-red-600 flex items-center gap-2">
                  <XCircle className="h-5 w-5" />
                  {t.notFound} ({results.not_found.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-h-48 overflow-auto">
                  <div className="flex flex-wrap gap-2">
                    {results.not_found.map((domain, idx) => (
                      <Badge key={idx} variant="outline" className="bg-red-50 text-red-700 border-red-200">
                        {domain}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default MediaSearchTab;
